import { GripVertical, ArrowUp, ArrowDown, Eye, EyeOff, Edit, Trash2, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdmin } from './AdminContext';

export const AdminHomepageTab = () => {
  const {
    homepageSections, moveSection, toggleSectionVisibility, saveHomepageLayout, savingLayout,
    homepageContent, editingImage, setEditingImage, newImageUrl, setNewImageUrl,
    updateContentImage, deleteArtist, deleteEvent, deleteFoodTruck, deleteVendor,
    ads, deleteAd, setEditingAd, setAdDialogOpen
  } = useAdmin();

  const renderImageGrid = (items, type, idPrefix, deleteHandler, isAspectVideo = false) => (
    <div className={`grid ${isAspectVideo ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'} gap-2 sm:gap-4`}>
      {items?.map((item) => (
        <div key={item.id} className="relative group">
          <div className={`${isAspectVideo ? 'aspect-video' : 'aspect-square'} rounded-lg overflow-hidden bg-muted`}>
            {item.image_url ? (
              <img src={item.image_url} alt={item.name || item.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No Image</div>
            )}
          </div>
          <p className="text-xs sm:text-sm font-medium mt-1 sm:mt-2 truncate">{item.name || item.title}</p>
          {item.date && <p className="text-[10px] sm:text-xs text-muted-foreground">{item.date}</p>}
          {editingImage === `${idPrefix}-${item.id}` ? (
            <div className="mt-2 space-y-2">
              <Input value={newImageUrl} onChange={(e) => setNewImageUrl(e.target.value)} placeholder="Image URL..." className="text-xs h-8" />
              <div className="flex gap-1">
                <Button size="sm" className="flex-1 h-7 text-xs" onClick={() => updateContentImage(type, item.id, newImageUrl)}>Save</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditingImage(null); setNewImageUrl(''); }}>X</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-1 mt-1">
              <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => { setEditingImage(`${idPrefix}-${item.id}`); setNewImageUrl(item.image_url || ''); }}>
                <Edit size={12} className="mr-1" /> Edit
              </Button>
              <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => deleteHandler(item.id)}>
                <Trash2 size={12} />
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Section Order Management */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><GripVertical size={20} /> Homepage Section Order</CardTitle>
          <CardDescription>Drag sections to reorder, toggle visibility</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {homepageSections.sort((a, b) => a.order - b.order).map((section, index) => (
              <div key={section.id} className={`flex items-center justify-between p-3 rounded-lg border ${section.visible ? 'bg-card border-border' : 'bg-muted/50 border-muted'}`}>
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSection(index, 'up')} disabled={index === 0}><ArrowUp size={14} /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSection(index, 'down')} disabled={index === homepageSections.length - 1}><ArrowDown size={14} /></Button>
                  </div>
                  <div>
                    <p className={`font-medium ${!section.visible && 'text-muted-foreground'}`}>{section.name}</p>
                    <p className="text-xs text-muted-foreground">Order: {section.order}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => toggleSectionVisibility(index)} className={section.visible ? 'text-green-500' : 'text-muted-foreground'}>
                  {section.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                  <span className="ml-2">{section.visible ? 'Visible' : 'Hidden'}</span>
                </Button>
              </div>
            ))}
          </div>
          <Button onClick={saveHomepageLayout} className="mt-4 w-full" disabled={savingLayout}>
            {savingLayout ? 'Saving...' : 'Save Layout Changes'}
          </Button>
        </CardContent>
      </Card>

      {/* Content Image Management */}
      <Card className="bg-card border-border">
        <CardHeader className="px-3 sm:px-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg"><Image size={18} /> Content Images</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Update or delete homepage content</CardDescription>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <Tabs defaultValue="artists-images" className="w-full">
            <div className="overflow-x-auto -mx-1 px-1">
              <TabsList className="inline-flex min-w-max mb-4 text-xs">
                <TabsTrigger value="artists-images" className="text-xs px-2">Artists</TabsTrigger>
                <TabsTrigger value="events-images" className="text-xs px-2">Events</TabsTrigger>
                <TabsTrigger value="trucks-images" className="text-xs px-2">Trucks</TabsTrigger>
                <TabsTrigger value="vendors-images" className="text-xs px-2">Vendors</TabsTrigger>
                <TabsTrigger value="ads-images" className="text-xs px-2">Ads</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="artists-images">{renderImageGrid(homepageContent?.artists, 'artist', 'artist', deleteArtist)}</TabsContent>
            <TabsContent value="events-images">{renderImageGrid(homepageContent?.events, 'event', 'event', deleteEvent, true)}</TabsContent>
            <TabsContent value="trucks-images">{renderImageGrid(homepageContent?.food_trucks, 'food_truck', 'food_truck', deleteFoodTruck)}</TabsContent>
            <TabsContent value="vendors-images">{renderImageGrid(homepageContent?.vendors, 'vendor', 'vendor', deleteVendor)}</TabsContent>
            <TabsContent value="ads-images">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                {ads?.map((ad) => (
                  <div key={ad.id} className="relative group">
                    <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                      {ad.image_url ? <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No Image</div>}
                    </div>
                    <p className="text-xs sm:text-sm font-medium mt-1 sm:mt-2 truncate">{ad.title}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground truncate">{ad.business_name}</p>
                    <div className="flex gap-1 mt-1">
                      <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => { setEditingAd(ad); setAdDialogOpen(true); }}>
                        <Edit size={12} className="mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => deleteAd(ad.id)}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
